import React, { useState, useEffect, useCallback } from 'react';
import { blogAPI, consultationAPI, quizAPI, pageViewsAPI } from '../../utils/api';
import { formatDate } from '../../utils/formatters';

interface DashboardStats {
  totalBlogPosts: number;
  publishedBlogPosts: number;
  draftBlogPosts: number;
  totalConsultations: number;
  pendingConsultations: number;
  completedConsultations: number;
  totalQuizzes: number;
  activeQuizzes: number;
  recentBlogPosts: any[];
  recentConsultations: any[];
  activityData: {
    labels: string[];
    blogData: number[];
    consultationData: number[];
  };
}

interface PageViewsData {
  totalViews: number;
  todayViews: number;
  lastWeekViews: number;
  lastMonthViews: number;
  topPages: {
    path: string;
    views: number;
  }[];
  viewsByDate: {
    date: string;
    views: number;
  }[];
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalBlogPosts: 0,
    publishedBlogPosts: 0,
    draftBlogPosts: 0,
    totalConsultations: 0,
    pendingConsultations: 0,
    completedConsultations: 0,
    totalQuizzes: 0,
    activeQuizzes: 0,
    recentBlogPosts: [],
    recentConsultations: [],
    activityData: {
      labels: [],
      blogData: [],
      consultationData: []
    }
  });
  const [pageViewsData, setPageViewsData] = useState<PageViewsData>({
    totalViews: 0,
    todayViews: 0,
    lastWeekViews: 0,
    lastMonthViews: 0,
    topPages: [],
    viewsByDate: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [pageViewsLoading, setPageViewsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageViewsError, setPageViewsError] = useState<string | null>(null);
  const [chartView, setChartView] = useState<'both' | 'blog' | 'consultation'>('both');

  // Function to fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch blog posts
      const blogResponse = await blogAPI.getPosts(1, 100);
      
      // Extract blog posts from the response
      const blogPosts = blogResponse.data.blogPosts || blogResponse.data || [];
      
      // Process the posts to ensure published is a boolean
      const processedPosts = Array.isArray(blogPosts) ? blogPosts.map((post: any) => ({
        ...post,
        published: post.published === true
      })) : [];
      
      // Calculate stats
      const publishedPostsFiltered = processedPosts.filter((post: any) => post.published === true);
      const draftPosts = processedPosts.filter((post: any) => post.published === false);
      
      // Fetch consultations
      const consultationsResponse = await consultationAPI.getConsultations(1, 100);
      const consultations = consultationsResponse.data.consultations || [];
      
      // Fetch quizzes
      const quizzesResponse = await quizAPI.getAllQuizzes(1, 100);
      const quizzes = quizzesResponse.data.quizzes || quizzesResponse.data || [];

      // Calculate stats
      const pendingConsultations = consultations.filter((c: any) => c.status === 'pending' || c.status === 'confirmed');
      const completedConsultations = consultations.filter((c: any) => c.status === 'completed');
      const activeQuizzes = quizzes.filter((q: any) => q.active || q.isActive);

      // Sort by date and get recent items
      const sortedPosts = [...processedPosts].sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const sortedConsultations = [...consultations].sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Generate activity data for the last 6 months
      const activityData = generateActivityData(processedPosts, consultations);

      setStats({
        totalBlogPosts: processedPosts.length,
        publishedBlogPosts: publishedPostsFiltered.length,
        draftBlogPosts: draftPosts.length,
        totalConsultations: consultations.length,
        pendingConsultations: pendingConsultations.length,
        completedConsultations: completedConsultations.length,
        totalQuizzes: quizzes.length,
        activeQuizzes: activeQuizzes.length,
        recentBlogPosts: sortedPosts.slice(0, 5),
        recentConsultations: sortedConsultations.slice(0, 5),
        activityData
      });

      setIsLoading(false);
    } catch (err: any) {
      setError('Der opstod en fejl ved indlæsning af dashboard data.');
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      try {
        // Check if user is authenticated and is admin
        await fetchDashboardData();
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    
    checkAuthAndFetchData();
  }, [fetchDashboardData]);

  // Function to fetch page views data
  const fetchPageViewsData = useCallback(async () => {
    try {
      setPageViewsLoading(true);
      setPageViewsError(null);

      // Create a proper date formatter
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Get current date
      const today = new Date();
      const todayStr = formatDate(today);
      
      // Calculate one week ago from today
      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(today.getDate() - 7);
      const oneWeekAgoStr = formatDate(oneWeekAgo);
      
      // Calculate one month ago from today
      const oneMonthAgo = new Date(today);
      oneMonthAgo.setMonth(today.getMonth() - 1);
      const oneMonthAgoStr = formatDate(oneMonthAgo);
      
      // Get general page views statistics (total and today)
      const pageViewsResponse = await pageViewsAPI.getPageViews();
      
      // Get page views by page
      const pageViewsByPageResponse = await pageViewsAPI.getPageViewsByPage();
      
      // Get page views for different time periods
      const lastWeekResponse = await pageViewsAPI.getPageViewsByDateRange(oneWeekAgoStr, todayStr);
      const lastMonthResponse = await pageViewsAPI.getPageViewsByDateRange(oneMonthAgoStr, todayStr);
      
      // Process the data
      const pageViews = pageViewsResponse.data || { total: 0, today: 0 };
      const topPages = pageViewsByPageResponse.data || [];
      
      // Calculate the views for last week and last month
      let lastWeekViews = 0;
      let lastMonthViews = 0;
      
      if (lastWeekResponse.data) {
        if (typeof lastWeekResponse.data.total === 'number') {
          lastWeekViews = lastWeekResponse.data.total;
        } else if (Array.isArray(lastWeekResponse.data.viewsByDate)) {
          lastWeekViews = lastWeekResponse.data.viewsByDate.reduce(
            (sum: number, item: { views: number }) => sum + (item.views || 0), 
            0
          );
        }
      }
      
      if (lastMonthResponse.data) {
        if (typeof lastMonthResponse.data.total === 'number') {
          lastMonthViews = lastMonthResponse.data.total;
        } else if (Array.isArray(lastMonthResponse.data.viewsByDate)) {
          lastMonthViews = lastMonthResponse.data.viewsByDate.reduce(
            (sum: number, item: { views: number }) => sum + (item.views || 0), 
            0
          );
        }
      }
      
      setPageViewsData({
        totalViews: pageViews.total || 0,
        todayViews: pageViews.today || 0,
        lastWeekViews,
        lastMonthViews,
        topPages: topPages.slice(0, 5), // Top 5 pages
        viewsByDate: [] // We don't need this anymore
      });
      
      setPageViewsLoading(false);
    } catch (err: any) {
      setPageViewsError('Der opstod en fejl ved indlæsning af besøgsstatistik. Kontroller at API-endepunkterne er tilgængelige.');
      setPageViewsLoading(false);
    }
  }, []);

  useEffect(() => {
    // This will run once when the component mounts
    fetchPageViewsData();
  }, [fetchPageViewsData]);

  // Generate activity data for the last 6 months
  const generateActivityData = (blogPosts: any[], consultations: any[]) => {
    const months: string[] = [];
    const blogData: number[] = [];
    const consultationData: number[] = [];
    
    // Get the last 6 months
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = month.toLocaleDateString('da-DK', { month: 'short' });
      months.push(monthName);
      
      // Count blog posts for this month
      const postsInMonth = blogPosts.filter((post: any) => {
        const postDate = new Date(post.createdAt);
        return postDate.getMonth() === month.getMonth() && 
               postDate.getFullYear() === month.getFullYear();
      }).length;
      
      // Count consultations for this month
      const consultationsInMonth = consultations.filter((consultation: any) => {
        const consultationDate = new Date(consultation.createdAt);
        return consultationDate.getMonth() === month.getMonth() && 
               consultationDate.getFullYear() === month.getFullYear();
      }).length;
      
      blogData.push(postsInMonth);
      consultationData.push(consultationsInMonth);
    }
    
    return { labels: months, blogData, consultationData };
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <h2 className="text-xl md:text-2xl font-bold text-gray-800">Dashboard</h2>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Blog Statistik</h3>
          <div className="space-y-2 md:space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Totale indlæg</span>
              <span className="font-bold text-lg md:text-xl">{stats.totalBlogPosts}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Publicerede</span>
              <span className="font-bold text-green-600">{stats.publishedBlogPosts}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Kladder</span>
              <span className="font-bold text-yellow-600">{stats.draftBlogPosts}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Konsultation Statistik</h3>
          <div className="space-y-2 md:space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Totale konsultationer</span>
              <span className="font-bold text-lg md:text-xl">{stats.totalConsultations}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Afventende</span>
              <span className="font-bold text-blue-600">{stats.pendingConsultations}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Gennemført</span>
              <span className="font-bold text-green-600">{stats.completedConsultations}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Quiz Statistik</h3>
          <div className="space-y-2 md:space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Totale quizzer</span>
              <span className="font-bold text-lg md:text-xl">{stats.totalQuizzes}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base text-gray-600">Aktive quizzer</span>
              <span className="font-bold text-green-600">{stats.activeQuizzes}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Seneste Blog Indlæg</h3>
          {stats.recentBlogPosts.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {stats.recentBlogPosts.map((post: any) => (
                <li key={post._id} className="py-2 md:py-3">
                  <div className="flex justify-between">
                    <div className="pr-2">
                      <p className="font-medium text-sm md:text-base text-gray-800 line-clamp-1">{post.title}</p>
                      <p className="text-xs md:text-sm text-gray-500">
                        {post.published ? 'Publiceret' : 'Kladde'} • {formatDate(post.createdAt)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs self-start whitespace-nowrap ${
                      post.published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {post.published ? 'Publiceret' : 'Kladde'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-4 text-sm md:text-base">Ingen blog indlæg fundet.</p>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Seneste Konsultationer</h3>
          {stats.recentConsultations.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {stats.recentConsultations.map((consultation: any) => (
                <li key={consultation._id} className="py-2 md:py-3">
                  <div className="flex justify-between">
                    <div className="pr-2">
                      <p className="font-medium text-sm md:text-base text-gray-800 line-clamp-1">{consultation.name}</p>
                      <p className="text-xs md:text-sm text-gray-500">
                        {consultation.dogName} • {formatDate(consultation.createdAt)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs self-start whitespace-nowrap ${
                      consultation.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : consultation.status === 'confirmed'
                        ? 'bg-blue-100 text-blue-800'
                        : consultation.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {consultation.status === 'pending'
                        ? 'Afventer'
                        : consultation.status === 'confirmed'
                        ? 'Bekræftet'
                        : consultation.status === 'completed'
                        ? 'Gennemført'
                        : 'Annulleret'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-4 text-sm md:text-base">Ingen konsultationer fundet.</p>
          )}
        </div>
      </div>
      
      {/* Activity Chart */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 md:mb-4 space-y-2 sm:space-y-0">
          <h3 className="text-base md:text-lg font-semibold text-gray-700">Aktivitet Over Tid</h3>
          
          {/* Chart view selection */}
          <div className="flex space-x-2 text-xs">
            <button 
              onClick={() => setChartView('both')}
              className={`px-2 py-1 rounded ${chartView === 'both' ? 'bg-gray-200 font-medium' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Begge
            </button>
            <button 
              onClick={() => setChartView('blog')}
              className={`px-2 py-1 rounded ${chartView === 'blog' ? 'bg-gray-200 font-medium' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Blog
            </button>
            <button 
              onClick={() => setChartView('consultation')}
              className={`px-2 py-1 rounded ${chartView === 'consultation' ? 'bg-gray-200 font-medium' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Konsultationer
            </button>
          </div>
        </div>
        
        <div className="h-64 md:h-80">
          {stats.activityData.labels.length > 0 ? (
            <div className="w-full h-full">
              {/* Chart bars */}
              <div className="flex h-[calc(100%-80px)] sm:h-[calc(100%-60px)] items-end space-x-1 sm:space-x-2 md:space-x-4">
                {stats.activityData.labels.map((month, index) => (
                  <div key={month} className="flex-1 flex flex-col items-center">
                    {/* Blog post bar with value */}
                    {(chartView === 'blog' || chartView === 'both') && (
                      <div className="relative w-full flex flex-col items-center">
                        <div 
                          className="w-full bg-snudefryd-blue rounded-t" 
                          style={{ 
                            height: `${Math.max(5, (stats.activityData.blogData[index] / Math.max(...stats.activityData.blogData, 1)) * 100)}%`,
                            minHeight: stats.activityData.blogData[index] > 0 ? '10px' : '0'
                          }}
                        >
                          {stats.activityData.blogData[index] > 0 && (
                            <span className="absolute -top-5 sm:-top-6 left-1/2 transform -translate-x-1/2 text-[10px] sm:text-xs font-medium text-gray-700">
                              {stats.activityData.blogData[index]}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Consultation bar with value */}
                    {(chartView === 'consultation' || chartView === 'both') && (
                      <div className="relative w-full flex flex-col items-center mt-1">
                        <div 
                          className="w-full bg-snudefryd-purple rounded-t" 
                          style={{ 
                            height: `${Math.max(5, (stats.activityData.consultationData[index] / Math.max(...stats.activityData.consultationData, 1)) * 100)}%`,
                            minHeight: stats.activityData.consultationData[index] > 0 ? '10px' : '0'
                          }}
                        >
                          {stats.activityData.consultationData[index] > 0 && (
                            <span className="absolute -top-5 sm:-top-6 left-1/2 transform -translate-x-1/2 text-[10px] sm:text-xs font-medium text-gray-700">
                              {stats.activityData.consultationData[index]}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Month label */}
                    <div className="text-[10px] sm:text-xs text-gray-600 mt-2">{month}</div>
                  </div>
                ))}
              </div>
              
              {/* Y-axis labels */}
              <div className="flex flex-col sm:flex-row sm:justify-between mt-4 border-t border-gray-200 pt-2 space-y-2 sm:space-y-0">
                <div className="text-[10px] sm:text-xs text-gray-500">
                  <span className="font-medium">Total:</span> {stats.activityData.blogData.reduce((a, b) => a + b, 0)} blog, {stats.activityData.consultationData.reduce((a, b) => a + b, 0)} konsultationer
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500">
                  <span className="font-medium">Gns. pr. måned:</span> {(stats.activityData.blogData.reduce((a, b) => a + b, 0) / stats.activityData.labels.length).toFixed(1)} blog, {(stats.activityData.consultationData.reduce((a, b) => a + b, 0) / stats.activityData.labels.length).toFixed(1)} konsultationer
                </div>
              </div>
              
              {/* Legend */}
              <div className="flex justify-center items-center mt-2 space-x-6">
                {(chartView === 'blog' || chartView === 'both') && (
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-snudefryd-blue rounded-sm mr-2"></div>
                    <span className="text-[10px] sm:text-xs text-gray-600">Blog indlæg</span>
                  </div>
                )}
                {(chartView === 'consultation' || chartView === 'both') && (
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-snudefryd-purple rounded-sm mr-2"></div>
                    <span className="text-[10px] sm:text-xs text-gray-600">Konsultationer</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
              <p className="text-gray-500 text-sm md:text-base px-4 text-center">
                Ingen aktivitetsdata tilgængelig.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Page Views Section */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 md:mb-4 space-y-2 sm:space-y-0">
          <h3 className="text-base md:text-lg font-semibold text-gray-700">Besøgsstatistik</h3>
        </div>

        {pageViewsLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
          </div>
        ) : pageViewsError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {pageViewsError}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Page Views Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <div className="bg-gray-50 rounded-lg p-3 md:p-4 text-center">
                <p className="text-xs md:text-sm text-gray-500">Totale besøg</p>
                <p className="text-xl md:text-2xl font-bold text-snudefryd-blue mt-1">{pageViewsData.totalViews.toLocaleString('da-DK')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 md:p-4 text-center">
                <p className="text-xs md:text-sm text-gray-500">I dag</p>
                <p className="text-xl md:text-2xl font-bold text-snudefryd-blue mt-1">{pageViewsData.todayViews.toLocaleString('da-DK')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 md:p-4 text-center">
                <p className="text-xs md:text-sm text-gray-500">Sidste 7 dage</p>
                <p className="text-xl md:text-2xl font-bold text-snudefryd-blue mt-1">{pageViewsData.lastWeekViews.toLocaleString('da-DK')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 md:p-4 text-center">
                <p className="text-xs md:text-sm text-gray-500">Sidste 30 dage</p>
                <p className="text-xl md:text-2xl font-bold text-snudefryd-blue mt-1">{pageViewsData.lastMonthViews.toLocaleString('da-DK')}</p>
              </div>
            </div>

            {/* Top Pages */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Mest besøgte sider</h4>
              {pageViewsData.topPages.length > 0 ? (
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="px-3 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Side
                          </th>
                          <th scope="col" className="px-3 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Besøg
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pageViewsData.topPages.map((page, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-900 truncate max-w-[150px] md:max-w-xs">
                              {page.path}
                            </td>
                            <td className="px-3 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-900 text-right">
                              {page.views.toLocaleString('da-DK')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : pageViewsData.totalViews > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-gray-500 text-sm">
                    Indsamler data om mest besøgte sider...
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4 text-sm">
                  Ingen sidevisningsdata tilgængelig.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 